import { computed, ref, unref, watch, type MaybeRef } from 'vue'
import axios, { type CancelTokenSource } from 'axios'
import { MaxPages, PageSize } from '@/constants'
import type { AppStoreModuleName } from '@/store/types'
import type { IFilter, IPagedResult, ISort } from '@/types'
import { SortOrder } from '@/types'
import { useRobaComponent } from '@/composables/useRobaComponent'

type FetchParams = {
  pageNumber: number
  searchQuery: string
  searchType: string
  filters: IFilter[]
  sortBy: ISort
}

function normalizeFilters(filters: IFilter[]): IFilter[] {
  return [...(filters || [])]
    .map(filter => ({ ...filter }))
    .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))
}

function makeKey(params: FetchParams): string {
  return JSON.stringify({
    p: params.pageNumber,
    q: params.searchQuery.trim(),
    t: params.searchType,
    f: normalizeFilters(params.filters),
    s: {
      field: params.sortBy?.sortField || '',
      order: params.sortBy?.sortOrder ?? SortOrder.asc,
    },
  })
}

export function buildVisiblePageTokens(currentPage: number, totalPages: number): Array<number | string> {
  if (totalPages <= 1) return [1]

  const pages = new Set<number>([1, totalPages, currentPage])

  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    if (page > 1 && page < totalPages) pages.add(page)
  }

  const sortedPages = [...pages].sort((a, b) => a - b)
  const tokens: Array<number | string> = []

  sortedPages.forEach((page, index) => {
    const previous = sortedPages[index - 1]
    if (previous && page - previous > 1) tokens.push('...')
    tokens.push(page)
  })

  return tokens
}

export function useRobaPagedComponent<TEntity, TData extends IPagedResult<TEntity>>(
  storeName: MaybeRef<AppStoreModuleName>,
) {
  const roba = useRobaComponent(storeName)

  const currentPage = ref(1)
  const pageSize = ref(PageSize)
  const maxPages = ref(MaxPages)
  const searchQuery = ref('')
  const searchType = ref('')
  const filters = ref<IFilter[]>([])
  const sortBy = ref<ISort>({ sortOrder: SortOrder.asc, sortField: '' })
  const cancelTokenSource = ref<CancelTokenSource | null>(null)

  let timeout: ReturnType<typeof setTimeout> | undefined
  let lastKey: string | null = null

  const entities = computed<TEntity[]>(() => {
    return ((roba.currentState.value.data as TData | undefined)?.content ?? []) as TEntity[]
  })

  const totalPages = computed<number>(() => {
    return ((roba.currentState.value.data as TData | undefined)?.totalPages ?? 0) as number
  })

  const showNoResults = computed<boolean>(() => {
    return roba.storeData.value !== undefined && !roba.isLoading.value && entities.value.length === 0
  })

  const showPagination = computed<boolean>(() => totalPages.value > 1)
  const visiblePageTokens = computed(() => buildVisiblePageTokens(currentPage.value, totalPages.value))

  function clearPendingFetch(): void {
    if (timeout) clearTimeout(timeout)
  }

  function scheduleFetch(): void {
    clearPendingFetch()
    timeout = setTimeout(() => {
      void fetchDataPagedWithCancelToken()
    }, 300)
  }

  function buildParams(): FetchParams {
    return {
      pageNumber: currentPage.value,
      searchQuery: searchQuery.value,
      searchType: searchType.value,
      filters: filters.value,
      sortBy: sortBy.value,
    }
  }

  async function fetchDataPaged(): Promise<void> {
    await roba.store.value.dispatch(`${unref(storeName)}/fetchDataPaged`, buildParams())
  }

  async function fetchDataPagedWithCancelToken(): Promise<void> {
    const params = buildParams()
    const key = makeKey(params)

    if (lastKey === key) return
    lastKey = key

    if (cancelTokenSource.value) {
      cancelTokenSource.value.cancel('superseded')
    }

    cancelTokenSource.value = axios.CancelToken.source()

    try {
      await roba.store.value.dispatch(`${unref(storeName)}/fetchDataPagedWithCancelToken`, {
        ...params,
        cancelToken: cancelTokenSource.value.token,
      })
    } catch (error) {
      if (!axios.isCancel?.(error)) {
        throw error
      }
    }
  }

  watch(sortBy, () => {
    currentPage.value = 1
    scheduleFetch()
  }, { deep: true })

  watch(searchType, () => {
    currentPage.value = 1
    scheduleFetch()
  })

  watch(filters, () => {
    currentPage.value = 1
    scheduleFetch()
  }, { deep: true })

  watch(searchQuery, () => {
    scheduleFetch()
  })

  watch(currentPage, () => {
    void fetchDataPagedWithCancelToken()
  })

  function goToPage(token: number | string): void {
    if (typeof token !== 'number' || token === currentPage.value) return
    currentPage.value = token
  }

  function goToPreviousPage(): void {
    if (currentPage.value > 1) currentPage.value -= 1
  }

  function goToNextPage(): void {
    if (currentPage.value < totalPages.value) currentPage.value += 1
  }

  return {
    ...roba,
    cancelTokenSource,
    currentPage,
    entities,
    fetchDataPaged,
    fetchDataPagedWithCancelToken,
    filters,
    goToNextPage,
    goToPage,
    goToPreviousPage,
    maxPages,
    pageSize,
    searchQuery,
    searchType,
    showNoResults,
    showPagination,
    sortBy,
    totalPages,
    visiblePageTokens,
  }
}
